import {
  Alert,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  Grid,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { Input, SubmitButton } from "components";
import { ResetPassword, ResetPasswordViewResource, ModelPropertyBase } from "generated/openApi";
import produce from "immer";
import { the404PageRoute } from "pages/The404Page/The404Page";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { loginPageRoute } from "../LoginPage/LoginPage";

const routeName = "resetPassword";

class Params {
  tenantName: string | undefined = undefined;
  userId: number | undefined = undefined;
  validationToken: string | undefined = undefined;
}

export const resetPasswordPageRoute: PageRoute<Params> = {
  handle: "Reset Password",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const ResetPasswordPage = () => {
  const { params } = useParams(routeName, Params);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [resetPassword, setResetPassword] = useState<ResetPassword>({} as ResetPassword);
  const [viewResource, setViewResource] = useState<ResetPasswordViewResource>({});
  const properties = viewResource.properties || [];
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const navigate = useNavigate();

  useEffect(() => {
    fetch();
    fetchViewResource();
  }, []);

  const fetch = () => {
    setValidationErrors({});
    ApiService.auth
      .getResetPassword(params.tenantName, params.userId, params.validationToken)
      .then((response) => {
        setResetPassword(response);
      })
      .catch(() => {
        navigate({
          pathname: `/${the404PageRoute.getPath()}`,
        });
      });
  };

  const fetchViewResource = () => {
    ApiService.auth.getResetPasswordViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const post = () => {
    setValidationErrors({});

    ApiService.auth
      .postResetPassword(resetPassword)
      .then(() => {
        dispatch(setAppToast(newToast("success")));

        navigate({
          pathname: `/${loginPageRoute.getPath()}`,
        });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  const tenantNameField = () => {
    const propertyName = pn<ResetPassword>("tenantName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={resetPassword?.tenantName}
        onChange={(e) =>
          setResetPassword(
            produce((draft) => {
              draft.tenantName = e.target.value;
            })
          )
        }
        disabled={true}
      />
    );
  };

  const usernameField = () => {
    const propertyName = pn<ResetPassword>("username");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={resetPassword?.username}
        onChange={(e) =>
          setResetPassword(
            produce((draft) => {
              draft.username = e.target.value;
            })
          )
        }
        disabled={true}
      />
    );
  };

  const newPasswordField = () => {
    const propertyName = pn<ResetPassword>("newPassword");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={resetPassword?.newPassword}
        onChange={(e) =>
          setResetPassword(
            produce((draft) => {
              draft.newPassword = e.target.value;
            })
          )
        }
        type="password"
      />
    );
  };

  const confirmNewPasswordField = () => {
    const propertyName = pn<ResetPassword>("confirmNewPassword");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={resetPassword?.confirmNewPassword}
        onChange={(e) =>
          setResetPassword(
            produce((draft) => {
              draft.confirmNewPassword = e.target.value;
            })
          )
        }
        type="password"
      />
    );
  };

  return (
    <Page>
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Card sx={{ maxWidth: "550px", width: "100%" }}>
          <Stack px={3} py={7} spacing="lg">
            <Stack spacing="sm" alignItems="center">
              <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                Reset Your Password
              </Typography>
            </Stack>

            <Form>
              <Stack spacing="md">
                {validationErrors?.[pn<ResetPassword>("generalValidationErrors")] && (
                  <Alert severity="error">{validationErrors?.[pn<ResetPassword>("generalValidationErrors")]?.[0]}</Alert>
                )}

                {tenantNameField()}
                {usernameField()}
                {newPasswordField()}
                {confirmNewPasswordField()}

                <SubmitButton onClick={post} label="Reset Password" fullWidth sx={{ height: "50px" }} />
              </Stack>
            </Form>
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};
